import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion as Motion } from 'framer-motion';
import { useLearner } from '../../hooks/useLearner';
import { useProgress } from '../../hooks/useProgress';
import useBuddy from '../../hooks/useBuddy';
import LevelRing from '../../components/map/LevelRing';
import audioManager from '../../lib/audioManager';
import { SPRING_POP, SPRING_SNAPPY } from '../../constants/motion';
import s from './RoundCompleteScreen.module.css';

/**
 * Round-complete screen for writing practice (FP-BUDDY-POLISH-01 §C).
 * Hero unit = the map's LevelRing with the buddy (ROUND_COMPLETE slot) rendered
 * on top of it (z-above, feet overlapping the ring's top arc). Live progression
 * is fetched internally via useProgress so the ring reflects real level + XP.
 * Below: "Round N Complete" + the actual "+N XP" earned, per-character tiles,
 * and the CTAs.
 *
 * @param {number}   roundNumber       The round that just finished (1-based).
 * @param {number}   xpEarned          XP earned this round.
 * @param {Array}    characters        Round results: { char, correct, ... }.
 * @param {boolean}  hasNextRound      Whether another round follows.
 * @param {function} onStartNextRound  Start the next round.
 * @param {function} onSeeSummary      Go to the practice summary.
 */
export default function RoundCompleteScreen({
  roundNumber,
  xpEarned,
  characters = [],
  hasNextRound,
  onStartNextRound,
  onSeeSummary,
}) {
  const { t } = useTranslation();
  const { data: activeLearner } = useLearner();
  const { data: progress } = useProgress(activeLearner?.id);
  const buddy = useBuddy();

  // Round-complete fanfare (audio-system.md) — fires once when the screen appears.
  useEffect(() => {
    audioManager.playSFX('round-complete');
  }, []);

  // Same fields the map's LearnerSidebar / StoryShipOverlay read.
  const currentLevel = progress?.level?.current ?? 0;
  const xpProgressPct = progress?.level?.xp_progress_pct ?? 0;
  const xpTotal = progress?.level?.xp_total ?? 0;
  const nextThreshold = progress?.level?.xp_at_next_level ?? 0;

  // When the learner earned nothing this round, the primary CTA invites a
  // retry rather than advancing.
  const primaryLabel = hasNextRound
    ? (xpEarned === 0 ? t('practice.tryAgain') : t('practice.startRound', { n: roundNumber + 1 }))
    : null;

  return (
    <div className={s.screen}>
      {/* Hero: level ring with the buddy z-above, feet overlapping the arc. */}
      <div className={s.hero}>
        <div className={s.ringWrap}>
          <LevelRing level={currentLevel} progressPct={xpProgressPct} />
          <span className={s.ringXp}>
            {t('practice.ringProgress', { known: xpTotal, threshold: nextThreshold })}
          </span>
        </div>

        {buddy.roundComplete && (
          <Motion.img
            src={buddy.roundComplete}
            alt=""
            aria-hidden="true"
            className={s.buddy}
            onError={(e) => { e.target.style.display = 'none'; }}
            initial={{ opacity: 0, y: 24, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ ...SPRING_POP, delay: 0.1 }}
          />
        )}
      </div>

      <Motion.h2
        className={s.title}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...SPRING_SNAPPY, delay: 0.15 }}
      >
        {t('practice.roundComplete', { n: roundNumber })}
      </Motion.h2>
      <p className={s.subtitle}>{t('practice.plusXp', { xp: xpEarned })}</p>

      {/* Per-character status tiles */}
      {characters.length > 0 && (
        <div className={s.charGrid}>
          {characters.map((c, i) => (
            <div key={c.characterId || i} className={s.charTile}>
              <span className={s.charGlyph}>{c.char}</span>
              <span
                className={`${s.statusDot} ${c.correct ? s.dotCorrect : s.dotMissed}`}
                aria-label={c.correct ? t('practice.perfect') : undefined}
              />
            </div>
          ))}
        </div>
      )}

      <div className={s.actions}>
        {primaryLabel && (
          <button type="button" className={s.primaryButton} onClick={onStartNextRound}>
            {primaryLabel}
          </button>
        )}
        <button type="button" className={s.secondaryButton} onClick={onSeeSummary}>
          {t('practice.seeSummary')}
        </button>
      </div>
    </div>
  );
}
